import { createReducer, on } from '@ngrx/store';

import { LocationInterface } from '../../models/location-interface';
import { LocationType } from '../../services/location.service';
import { MapMode } from '../../services/location-map.service';
import { GeoJSON } from '../../models/geojson';
import * as locationActions from '../actions';

export const locationFeatureKey = 'location';

// Location lists are fetched with different `fields` projections (the map page
// asks for far more than the settings pages), so a refetch merges into the
// entry it refreshes instead of being dropped or replacing it wholesale.
function mergeLocationsById(
  current: LocationInterface[],
  incoming: LocationInterface[],
): LocationInterface[] {
  const incomingById = new Map(
    incoming.map((location) => [location.id, location]),
  );
  const merged = current.map((location) => {
    const refreshed = incomingById.get(location.id);
    if (!refreshed) return location;
    incomingById.delete(location.id);
    return { ...location, ...refreshed };
  });
  return [...merged, ...incomingById.values()];
}

export interface State {
  mapMode: MapMode;
  selectedFeature: GeoJSON | null;
  featureList: LocationInterface[];
  locationTypes: LocationType[];
  locationLoading: boolean;
  typesLoading: boolean;
  locationTypeFilter: LocationType;
  count: number;
  locationError: any;
}

export const initialState: State = {
  mapMode: MapMode.Default,
  selectedFeature: null,
  featureList: [],
  locationTypes: [],
  locationError: null,
  locationLoading: false,
  typesLoading: false,
  locationTypeFilter: {
    id: -1,
    name: 'All locations',
    color: '#B0BEC5',
  },
  count: 0,
};

export const reducer = createReducer(
  initialState,
  on(locationActions.selectLocationTypeFilter, (state, action) => ({
    ...state,
    locationTypeFilter: action.locationType,
  })),
  on(locationActions.mapError, (state, action) => ({
    ...state,
    locationError: action.error,
    locationLoading: false,
  })),

  on(locationActions.setMapMode, (state, action) => ({
    ...state,
    mapMode: action.mapMode,
  })),

  on(locationActions.selectFeature, (state, action) => ({
    ...state,
    selectedFeature: action.locationFeature,
  })),

  on(locationActions.saveFeature, (state) => ({
    ...state,
    locationLoading: true,
  })),

  on(locationActions.saveFeatureSuccess, (state, action) => ({
    ...state,
    locationLoading: false,
    featureList: [...state.featureList, action.location],
    count: state.count + 1,
  })),

  on(locationActions.getLocations, (state) => ({
    ...state,
    locationLoading: true,
  })),

  on(locationActions.getLocationsSuccess, (state, action) => {
    return {
      ...state,
      featureList: mergeLocationsById(
        state.featureList,
        action.data.locationList,
      ),
      locationLoading: false,
      count: action.data.count,
    };
  }),

  on(locationActions.updateLocations, (state, action) => {
    return {
      ...state,
      featureList: mergeLocationsById(
        state.featureList,
        action.update.locationList,
      ),
      locationLoading: false,
      count: action.update.count,
    };
  }),

  on(locationActions.updateLocation, (state) => ({
    ...state,
    locationLoading: true,
  })),
  on(locationActions.eventSourceLocationUpdate, (state, { locations }) => {
    return {
      ...state,
      featureList: state.featureList.map(
        (location: LocationInterface) =>
          locations.find((updated) => updated.id === location.id) ?? location,
      ),
    };
  }),
  on(locationActions.updateLocationSuccess, (state, action) => {
    return {
      ...state,
      locationLoading: false,
      featureList: state.featureList.map((location: LocationInterface) => {
        if (location.id === action.location.id) {
          return action.location;
        }
        return location;
      }),
    };
  }),
  on(locationActions.deleteLocationSuccess, (state, action) => ({
    ...state,
    featureList: state.featureList.filter(
      (feature) => action.location.id !== feature.id,
    ),
    count: state.count - 1,
  })),
  on(locationActions.loadLocationTypes, (state) => ({
    ...state,
    typesLoading: true,
  })),
  on(locationActions.loadLocationTypesSuccess, (state, action) => ({
    ...state,
    locationTypes: action.locationTypes,
    typesLoading: false,
  })),

  on(locationActions.addNewType, (state) => ({
    ...state,
    locationLoading: true,
  })),
  on(locationActions.addNewTypeSuccess, (state, action) => ({
    ...state,
    locationTypes: [
      ...state.locationTypes,
      action.locationType as LocationType,
    ],
    locationLoading: false,
  })),

  on(locationActions.updateType, (state) => ({
    ...state,
    locationLoading: true,
  })),
  on(locationActions.updateTypeSuccess, (state, action) => ({
    ...state,
    locationTypes: state.locationTypes.map((type) =>
      action.locationType.id === type.id ? action.locationType : type,
    ),
    locationLoading: false,
  })),
  on(locationActions.deleteType, (state) => ({
    ...state,
    typesLoading: true,
  })),
  on(locationActions.deleteTypeSuccess, (state, action) => ({
    ...state,
    locationTypes: state.locationTypes.filter(
      (locationType) => action.locationType.id !== locationType.id,
    ),
    typesLoading: false,
  })),
);
export const getMapMode = (state: State) => state.mapMode;
export const getSelectedFeature = (state: State) => state.selectedFeature;
export const getFeatureList = (state: State) => state.featureList;
export const getLocationTypes = (state: State) => state.locationTypes;
export const getLocationError = (state: State) => state.locationError;
export const getLocationLoading = (state: State) => state.locationLoading;
export const getTypesLoading = (state: State) => state.locationLoading;
export const getLocationTypeFilter = (state: State) => state.locationTypeFilter;
export const getCount = (state: State) => state.count;
